import { acceptJsonHeaders, csrfHeaders, handleJson, jsonHeaders } from "@/lib/json-request"
import type { ShotFrameSource } from "@/lib/workspace-api"
import type { TaskFile, TaskMentions } from "@/types/tasks"

export type NoteAccess = "author" | "edit" | "read"
export type NoteShareAccess = "read" | "edit"
export type NoteShareTarget = "Project" | "Company" | "User"

export type NoteShareCompany = { id: number; name: string }

export type NoteShareProject = { id: number; name: string; cover_url: string | null }

export type NoteSharePerson = {
  user_id: number
  first_name: string | null
  last_name: string | null
  email: string
  name: string
}

export type NoteShareRow = {
  id: number
  target_type: NoteShareTarget
  target_id: number
  access: NoteShareAccess
  label: string
  active: boolean
  company: NoteShareCompany | null
  project: NoteShareProject | null
  person: NoteSharePerson | null
}

export type NoteDetail = {
  id: number
  title: string
  body: string
  access: NoteAccess
  updated_at: string
  files: TaskFile[]
  shares: NoteShareRow[]
  mentions: TaskMentions
}

export type NoteListRow = {
  id: number
  title: string
  updated_at: string
  shared: boolean
  pinned: boolean
  author: boolean
  snippet: string | null
}

export type NoteListCursor = {
  kind: "pin" | "note"
  at: string
  id: number
}

export type NoteListResponse = {
  ok: boolean
  status: "blank" | "too_short" | "ok"
  notes: NoteListRow[]
  next_cursor: NoteListCursor | null
}

export type NoteShareSearchTarget =
  | (NoteShareCompany & { type: "Company" })
  | (NoteShareProject & { type: "Project" })
  | {
      type: "User"
      id: number
      name: string
      first_name: string | null
      last_name: string | null
      email: string
    }

export type NoteShareTargetsPage = {
  ok: boolean
  targets: NoteShareSearchTarget[]
  next_offset: number | null
}
export type NoteTaskProject = { id: number; name: string; company_name: string }

export type NoteLibraryProject = { id: number; name: string; frame_format: string }

const NOTES_CHANGED = "notes:changed"

export function notifyNotesChanged() {
  window.dispatchEvent(new Event(NOTES_CHANGED))
}

export function onNotesChanged(listener: () => void) {
  window.addEventListener(NOTES_CHANGED, listener)
  return () => window.removeEventListener(NOTES_CHANGED, listener)
}

export async function fetchNotes(query: string, cursor?: NoteListCursor | null): Promise<NoteListResponse> {
  const params = new URLSearchParams()
  const trimmed = query.trim()
  if (trimmed) params.set("q", trimmed)
  if (cursor) {
    params.set("after_kind", cursor.kind)
    params.set("after_at", cursor.at)
    params.set("after_id", String(cursor.id))
  }
  const queryString = params.toString()
  const res = await fetch(`/notes${queryString ? `?${queryString}` : ""}`, {
    credentials: "same-origin",
    headers: acceptJsonHeaders(),
  })
  return handleJson(res)
}

export async function fetchNote(id: number): Promise<NoteDetail> {
  const res = await fetch(`/notes/${id}`, { credentials: "same-origin", headers: acceptJsonHeaders() })
  return (await handleJson<{ note: NoteDetail }>(res)).note
}

export async function createNote(input: { title?: string; body?: string } = {}): Promise<NoteDetail> {
  const res = await fetch("/notes", {
    method: "POST",
    credentials: "same-origin",
    headers: jsonHeaders(),
    body: JSON.stringify(input),
  })
  return (await handleJson<{ note: NoteDetail }>(res)).note
}

export async function updateNote(id: number, input: { title?: string; body?: string }): Promise<NoteDetail> {
  const res = await fetch(`/notes/${id}`, {
    method: "PATCH",
    credentials: "same-origin",
    headers: jsonHeaders(),
    body: JSON.stringify(input),
  })
  return (await handleJson<{ note: NoteDetail }>(res)).note
}

export async function deleteNote(id: number): Promise<void> {
  const res = await fetch(`/notes/${id}`, { method: "DELETE", credentials: "same-origin", headers: csrfHeaders() })
  await handleJson(res)
}

export async function pinNote(id: number): Promise<void> {
  const res = await fetch(`/notes/${id}/pin`, { method: "POST", credentials: "same-origin", headers: csrfHeaders() })
  await handleJson(res)
}

export async function unpinNote(id: number): Promise<void> {
  const res = await fetch(`/notes/${id}/pin`, { method: "DELETE", credentials: "same-origin", headers: csrfHeaders() })
  await handleJson(res)
}

export async function uploadNoteFile(id: number, file: File): Promise<NoteDetail> {
  const body = new FormData()
  body.append("file", file)
  const res = await fetch(`/notes/${id}/files`, {
    method: "POST",
    credentials: "same-origin",
    headers: csrfHeaders(),
    body,
  })
  return (await handleJson<{ note: NoteDetail }>(res)).note
}

export async function attachNoteFileFromLibrary(
  id: number,
  projectId: number,
  source: ShotFrameSource,
): Promise<NoteDetail> {
  const res = await fetch(`/notes/${id}/files`, {
    method: "POST",
    credentials: "same-origin",
    headers: jsonHeaders(),
    body: JSON.stringify({ project_id: projectId, source }),
  })
  return (await handleJson<{ note: NoteDetail }>(res)).note
}

export async function removeNoteFile(id: number, blobId: number): Promise<NoteDetail> {
  const res = await fetch(`/notes/${id}/files/${blobId}`, {
    method: "DELETE",
    credentials: "same-origin",
    headers: csrfHeaders(),
  })
  return (await handleJson<{ note: NoteDetail }>(res)).note
}

export async function createNoteShare(
  id: number,
  input: { target_type: NoteShareTarget; target_id: number; access: NoteShareAccess },
): Promise<NoteDetail> {
  const res = await fetch(`/notes/${id}/shares`, {
    method: "POST",
    credentials: "same-origin",
    headers: jsonHeaders(),
    body: JSON.stringify(input),
  })
  return (await handleJson<{ note: NoteDetail }>(res)).note
}

export async function deleteNoteShare(noteId: number, shareId: number): Promise<NoteDetail> {
  const res = await fetch(`/notes/${noteId}/shares/${shareId}`, {
    method: "DELETE",
    credentials: "same-origin",
    headers: csrfHeaders(),
  })
  return (await handleJson<{ note: NoteDetail }>(res)).note
}

export async function fetchNoteShareTargets(
  input: {
    q?: string
    offset?: number
    signal?: AbortSignal
  } = {},
): Promise<NoteShareTargetsPage> {
  const params = new URLSearchParams()
  const q = input.q?.trim()
  if (q) params.set("q", q)
  if (input.offset) params.set("offset", String(input.offset))
  const query = params.toString()
  const res = await fetch(`/notes/share_targets${query ? `?${query}` : ""}`, {
    credentials: "same-origin",
    headers: acceptJsonHeaders(),
    signal: input.signal,
  })
  return handleJson(res)
}

export async function fetchNoteLibraryProjects(): Promise<NoteLibraryProject[]> {
  const res = await fetch("/notes/library_projects", { credentials: "same-origin", headers: acceptJsonHeaders() })
  return (await handleJson<{ projects: NoteLibraryProject[] }>(res)).projects
}

export async function fetchNoteTaskProjects(): Promise<NoteTaskProject[]> {
  const res = await fetch("/notes/task_projects", { credentials: "same-origin", headers: acceptJsonHeaders() })
  return (await handleJson<{ projects: NoteTaskProject[] }>(res)).projects
}

export async function createNoteTask(
  id: number,
  input: { project_id: number; title: string; description: string; blob_ids: number[]; cut_body?: string },
): Promise<{ task_id: number; project_id: number; note: NoteDetail }> {
  const res = await fetch(`/notes/${id}/task`, {
    method: "POST",
    credentials: "same-origin",
    headers: jsonHeaders(),
    body: JSON.stringify(input),
  })
  return handleJson(res)
}

export async function searchNoteMentions(query: string, signal: AbortSignal): Promise<TaskMentions> {
  const res = await fetch(`/notes/mentions?q=${encodeURIComponent(query)}`, {
    credentials: "same-origin",
    headers: acceptJsonHeaders(),
    signal,
  })
  const data = await handleJson<TaskMentions & { ok: boolean }>(res)
  return { people: data.people ?? [], tasks: data.tasks ?? [], entities: data.entities ?? [] }
}
